/**
 * @copyright Copyright 2023-2025 Epic Systems Corporation
 * @file Actions buttons for an app's Showroom listing page
 * @module Epic.AppOrchard.Showroom.ActionButtons
 */

import { WarningTwoIcon } from "@chakra-ui/icons";
import { Box, Button, ButtonProps, ComponentWithAs, Grid, Text, useBreakpointValue } from "@chakra-ui/react";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import React, { FunctionComponent, memo } from "react";
import { FacilitatedAppAgreementButton, ShowroomRequestDownloadButton } from "../AppHelperComponents";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	headerColor: string;
	buttonBackgroundColor: string;
	buttonBorderColor: string;
	userSecurity: IUserSecurity;
	reload: () => void;
	isShowroom?: boolean;
}

export const ActionButtonsSection: FunctionComponent<IProps> = memo((props) => {
	const {
		appDetails,
		headerColor,
		buttonBackgroundColor,
		buttonBorderColor,
		userSecurity,
		isShowroom,
		reload,
	} = props;

	//#region state and props
	const appModel = appDetails.appModel;
	const isMobile = useBreakpointValue({ base: true, lg: false });

	const canPurchase = userSecurity.isEpicCustomer && userSecurity.canPurchase;
	const isOwnApp = appModel.organizationId === userSecurity.userOrganizationId;

	const showFacilitatedAgreementButton =
		isShowroom &&
		appDetails.appModel.isAppAgreementFacilitated &&
		canPurchase &&
		!isOwnApp &&
		!appModel.isRelatedOrgApp &&
		!userSecurity.getsAppLicensesFromOrganization_Name;

	const showParentDownloadWarning =
		canPurchase &&
		userSecurity.getsAppLicensesFromOrganization_Name &&
		!appDetails.isDownloaded &&
		appDetails.isDownloadedByParent;

	const isDownloadAvailable =
		userSecurity.canPurchase &&
		appModel.showDownloadLink &&
		!appModel.isAutoDownloadedClient &&
		!appDetails.isDownloaded &&
		!isOwnApp &&
		!appModel.requiresFutureEpicVersion &&
		!appModel.isRelatedOrgApp;
	const canOrgDownload =
		appDetails.previousAPILicenseExists ||
		appDetails.isUSCDIDownloadSet ||
		appDetails.appCanBeDownloadedWithoutApiLicense;
	const hasUnableToDownloadDisclaimer =
		isOwnApp || appModel.isRelatedOrgApp || appModel.requiresFutureEpicVersion;
	const showDownloadSection = isDownloadAvailable && (canOrgDownload || hasUnableToDownloadDisclaimer);

	//#endregion

	return (
		<>
			{showParentDownloadWarning && (
				<Text
					textAlign={"center"}
					fontWeight="400"
					fontStyle="italic"
					mt="8px"
					mb="20px"
					fontSize="18px"
				>
					<WarningTwoIcon color="yellow.500" marginRight="5px" mb="5px" />
					{`Your system automatically receives all apps activated by ${userSecurity.getsAppLicensesFromOrganization_Name}. ${userSecurity.getsAppLicensesFromOrganization_Name} has an existing download for this app.`}
				</Text>
			)}
			{userSecurity.isEpicCustomer && (
				<>
					{showFacilitatedAgreementButton && showDownloadSection && !isMobile ? (
						<Grid
							templateAreas={`"header1 header2"
											"middle1 middle2"
											"button1 button2"`}
							gap="0 6em"
						>
							<FacilitatedAppAgreementButton
								appDetails={appDetails}
								headerColor={headerColor}
								buttonBackgroundColor={buttonBackgroundColor}
								buttonBorderColor={buttonBorderColor}
								reload={reload}
								buttonsAs={ShowroomButton}
							/>
							<ShowroomRequestDownloadButton
								appDetails={appDetails}
								headerColor={headerColor}
								buttonBackgroundColor={buttonBackgroundColor}
								buttonBorderColor={buttonBorderColor}
								userSecurity={userSecurity}
								reload={reload}
								buttonsAs={isShowroom ? ShowroomButton : NormalButton}
								isShowroom={isShowroom}
							/>
						</Grid>
					) : (
						<>
							{showFacilitatedAgreementButton && (
								<Box w={"50%"}>
									<FacilitatedAppAgreementButton
										appDetails={appDetails}
										headerColor={headerColor}
										buttonBackgroundColor={buttonBackgroundColor}
										buttonBorderColor={buttonBorderColor}
										reload={reload}
										buttonsAs={ShowroomButton}
									/>
								</Box>
							)}

							{showDownloadSection && (
								<Box w={"50%"}>
									<ShowroomRequestDownloadButton
										appDetails={appDetails}
										headerColor={headerColor}
										buttonBackgroundColor={buttonBackgroundColor}
										buttonBorderColor={buttonBorderColor}
										userSecurity={userSecurity}
										reload={reload}
										buttonsAs={isShowroom ? ShowroomButton : NormalButton}
										isShowroom={isShowroom}
									/>
								</Box>
							)}
						</>
					)}
				</>
			)}
		</>
	);
});

const ShowroomButton: ComponentWithAs<"button", ButtonProps> = (props: ButtonProps) => {
	const { leftIcon, children, ...rest } = props;

	return (
		<Button {...rest} p="25px" fontWeight="500" fontSize="1.25rem" boxShadow="0px 0px 2px 1px #c5c5c5">
			{children}
		</Button>
	);
};

const NormalButton: ComponentWithAs<"button", ButtonProps> = (props: ButtonProps) => {
	const { leftIcon, children, ...rest } = props;

	return (
		<Button {...rest} fontWeight="500" colorScheme="blue">
			{children}
		</Button>
	);
};
